import {
    createContext,
    useContext,
    useState,
    useCallback,
    useEffect,
    type ReactNode,
} from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { config } from "@/lib/config";
import {
    setAccessToken,
    getAccessToken,
    clearTokens,
    decodeToken,
    isTokenExpired,
} from "./tokenStorage";
import { subscribeToRefreshRequests } from "./tokenRefreshManager";
import { createAuthErrorInfo, type AuthErrorInfo } from "./errors";

// User info extracted from platform token
export interface AuthUser {
    userId: string;
    email: string;
    tenantId: string;
    tenantName: string;
    roles: string[];
    permissions: string[];
}

// Tenant info for selection
export interface Tenant {
    id: string;
    name: string;
}

// Context for users who need to request access
export interface AccessRequestContext {
    userId: string;
    email: string;
    customerId: string | null;
    temporaryToken: string;
}

// Auth state exposed to the app
export interface AuthState {
    // User info from platform token
    user: AuthUser | null;

    // Available tenants (if multi-tenant user)
    availableTenants: Tenant[];

    // State flags
    isAuthenticated: boolean;
    isLoading: boolean;
    error: AuthErrorInfo | null;

    // Pending state for tenant selection
    pendingTenantSelection: boolean;

    // Access request flow state (user authenticated but no tenant access)
    requiresAccessRequest: boolean;
    accessRequestContext: AccessRequestContext | null;

    // Actions
    login: () => Promise<void>;
    logout: () => Promise<void>;
    selectTenant: (tenantId: string) => Promise<void>;
    switchTenant: (tenantId: string) => Promise<void>;
    refreshAccessToken: () => Promise<string>;
    clearError: () => void;
}

const AuthContext = createContext<AuthState | null>(null);

// Platform token payload structure
interface PlatformTokenPayload {
    sub: string;
    email: string;
    tenantId: string;
    tenantName: string;
    roles: string[];
    permissions: string[];
    exp: number;
}

// Token exchange response
interface TokenExchangeResponse {
    accessToken?: string;
    refreshToken?: string;
    expiresIn?: number;
    requiresTenantSelection?: boolean;
    tenants?: Tenant[];
    // Access request flow (user has no tenant access)
    requiresAccessRequest?: boolean;
    userId?: string;
    email?: string;
    customerId?: string | null;
    temporaryToken?: string;
    error?: {
        code: string;
        message: string;
    };
}

// Token refresh response
interface TokenRefreshResponse {
    accessToken: string;
    refreshToken: string;
    expiresIn: number;
    error?: {
        code: string;
        message: string;
    };
}

interface AuthProviderProps {
    children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
    const {
        isAuthenticated: auth0Authenticated,
        isLoading: auth0Loading,
        getAccessTokenSilently,
        loginWithRedirect,
        logout: auth0Logout,
    } = useAuth0();

    const [user, setUser] = useState<AuthUser | null>(null);
    const [availableTenants, setAvailableTenants] = useState<Tenant[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<AuthErrorInfo | null>(null);
    const [pendingTenantSelection, setPendingTenantSelection] = useState(false);
    const [pendingAuth0Token, setPendingAuth0Token] = useState<string | null>(
        null,
    );
    const [requiresAccessRequest, setRequiresAccessRequest] = useState(false);
    const [accessRequestContext, setAccessRequestContext] = useState<AccessRequestContext | null>(null);

    // Derive authentication state from having a valid user
    const isAuthenticated = user !== null;

    // Clear error
    const clearError = useCallback(() => {
        setError(null);
    }, []);

    // Parse user info from platform token
    const parseUserFromToken = useCallback((token: string): AuthUser => {
        const payload = decodeToken<PlatformTokenPayload>(token);
        return {
            userId: payload.sub,
            email: payload.email,
            tenantId: payload.tenantId,
            tenantName: payload.tenantName,
            roles: payload.roles,
            permissions: payload.permissions,
        };
    }, []);

    // Exchange Auth0 token for platform tokens
    const exchangeToken = useCallback(
        async (
            auth0Token: string,
            tenantId?: string,
        ): Promise<TokenExchangeResponse> => {
            const endpoint = tenantId
                ? `${config.apiUrl}/api/v1/auth/select-tenant`
                : `${config.apiUrl}/api/v1/auth/token-exchange`;

            const body = tenantId
                ? { temporaryToken: auth0Token, tenantId }
                : { auth0AccessToken: auth0Token };

            const response = await fetch(endpoint, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(body),
                credentials: "include", // For httpOnly cookie support
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(
                    data.error?.message || `Token exchange failed: ${response.status}`,
                );
            }

            return data;
        },
        [],
    );

    // Refresh the access token using refresh token (sent via httpOnly cookie)
    const refreshAccessToken = useCallback(async (): Promise<string> => {
        const response = await fetch(`${config.apiUrl}/api/v1/auth/refresh`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({}), // Empty body - refresh token sent via httpOnly cookie
            credentials: "include",
        });

        const data: TokenRefreshResponse = await response.json();

        if (!response.ok || data.error) {
            // Only dispatch session_expired if the user had an active session.
            // During initial auth (no token yet), this is just a failed probe — not a session loss.
            // Dispatching session_expired without an active session triggers auth0Logout → redirect → loop.
            const hadSession = !!getAccessToken();
            clearTokens();
            setUser(null);
            if (hadSession) {
                window.dispatchEvent(new CustomEvent("auth:session_expired"));
            }
            throw new Error(data.error?.message || "Token refresh failed");
        }

        setAccessToken(data.accessToken);
        // Note: New refresh token is stored in httpOnly cookie by backend
        return data.accessToken;
    }, []);

    // Setup auto-refresh timer
    useEffect(() => {
        if (!isAuthenticated) return;

        const accessToken = getAccessToken();
        if (!accessToken) return;

        // Refresh 1 minute before expiry
        const payload = decodeToken<{ exp: number }>(accessToken);
        const expiresAt = payload.exp * 1000;
        const refreshBuffer = 60 * 1000;
        const refreshAt = expiresAt - refreshBuffer;
        const delay = refreshAt - Date.now();

        if (delay <= 0) {
            // Token already expired or about to - refresh silently
            // Error handling is done inside refreshAccessToken (sets error state)
            refreshAccessToken().catch(() => {});
            return;
        }

        const timer = setTimeout(() => {
            // Background refresh - error handling done inside refreshAccessToken
            refreshAccessToken().catch(() => {});
        }, delay);

        return () => clearTimeout(timer);
    }, [isAuthenticated, refreshAccessToken]);

    // Wire up refresh function for authenticatedFetch via event subscription
    // Note: Empty dependency array is intentional - we register once on mount.
    // The handler captures refreshAccessToken via closure, which is stable.
    useEffect(() => {
        const unsubscribe = subscribeToRefreshRequests(refreshAccessToken);
        return unsubscribe;
    }, []);

    // Listen for session expiry events from authenticatedFetch
    useEffect(() => {
        const handleSessionExpired = () => {
            // Clear local state and redirect to login
            clearTokens();
            setUser(null);
            setAvailableTenants([]);
            setPendingTenantSelection(false);
            setPendingAuth0Token(null);
            setRequiresAccessRequest(false);
            setAccessRequestContext(null);
            // Auth0 logout will redirect to login
            auth0Logout({
                logoutParams: {
                    returnTo: window.location.origin,
                },
            });
        };

        const handleSessionIdleTimeout = () => {
            // Clear local state and redirect to login
            clearTokens();
            setUser(null);
            setAvailableTenants([]);
            setPendingTenantSelection(false);
            setPendingAuth0Token(null);
            setRequiresAccessRequest(false);
            setAccessRequestContext(null);
            // Store reason for display on login page
            try {
                sessionStorage.setItem("session_ended_reason", "idle_timeout");
            } catch {
                // Ignore storage errors
            }
            // Auth0 logout will redirect to login
            auth0Logout({
                logoutParams: {
                    returnTo: window.location.origin,
                },
            });
        };

        window.addEventListener("auth:session_expired", handleSessionExpired);
        window.addEventListener("auth:session_idle_timeout", handleSessionIdleTimeout);
        return () => {
            window.removeEventListener("auth:session_expired", handleSessionExpired);
            window.removeEventListener("auth:session_idle_timeout", handleSessionIdleTimeout);
        };
    }, [auth0Logout]);

    // Login - redirect to Auth0
    const login = useCallback(async () => {
        setError(null);
        await loginWithRedirect();
    }, [loginWithRedirect]);

    // Logout - clear platform session and Auth0
    const logout = useCallback(async () => {
        // Invalidate platform session (refresh token sent via httpOnly cookie)
        try {
            await fetch(`${config.apiUrl}/api/v1/auth/logout`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({}), // Empty body - refresh token sent via httpOnly cookie
                credentials: "include",
            });
        } catch {
            // Ignore errors - we're logging out anyway
        }

        // Clear local state
        clearTokens();
        setUser(null);
        setAvailableTenants([]);
        setPendingTenantSelection(false);
        setPendingAuth0Token(null);
        setRequiresAccessRequest(false);
        setAccessRequestContext(null);

        // Logout from Auth0
        auth0Logout({
            logoutParams: {
                returnTo: window.location.origin,
            },
        });
    }, [auth0Logout]);

    // Select tenant (during initial login with multiple tenants)
    const selectTenant = useCallback(
        async (tenantId: string) => {
            if (!pendingAuth0Token) {
                throw new Error("No pending authentication");
            }

            setIsLoading(true);
            setError(null);

            try {
                const result = await exchangeToken(pendingAuth0Token, tenantId);

                if (!result.accessToken) {
                    throw new Error("Invalid response from tenant selection");
                }

                setAccessToken(result.accessToken);
                // Note: Refresh token is stored in httpOnly cookie by backend
                setUser(parseUserFromToken(result.accessToken));
                setPendingTenantSelection(false);
                setPendingAuth0Token(null);
            } catch (e) {
                setError(createAuthErrorInfo(e));
                throw e;
            } finally {
                setIsLoading(false);
            }
        },
        [pendingAuth0Token, exchangeToken, parseUserFromToken],
    );

    // Switch tenant (for already authenticated users)
    const switchTenant = useCallback(
        async (tenantId: string) => {
            setIsLoading(true);
            setError(null);

            try {
                // Get a fresh Auth0 token
                const auth0Token = await getAccessTokenSilently();
                const result = await exchangeToken(auth0Token, tenantId);

                if (!result.accessToken) {
                    throw new Error("Invalid response from tenant switch");
                }

                setAccessToken(result.accessToken);
                // Note: Refresh token is stored in httpOnly cookie by backend
                setUser(parseUserFromToken(result.accessToken));
            } catch (e) {
                setError(createAuthErrorInfo(e));
                throw e;
            } finally {
                setIsLoading(false);
            }
        },
        [getAccessTokenSilently, exchangeToken, parseUserFromToken],
    );

    // Handle initial authentication flow
    useEffect(() => {
        const initAuth = async () => {
            // Wait for Auth0 to finish loading
            if (auth0Loading) return;

            // Check if we have an existing valid token in memory
            const existingToken = getAccessToken();

            if (existingToken && !isTokenExpired(existingToken)) {
                // Token still valid - restore user
                try {
                    setUser(parseUserFromToken(existingToken));
                    setIsLoading(false);
                    return;
                } catch {
                    // Token parsing failed - continue with refresh
                }
            }

            // Try to refresh using httpOnly cookie (if we have a session)
            // This will work if the user has a valid session cookie
            try {
                const newToken = await refreshAccessToken();
                setUser(parseUserFromToken(newToken));
                setIsLoading(false);
                return;
            } catch {
                // Refresh failed - no valid session, continue with Auth0 flow
                clearTokens();
            }

            // If Auth0 authenticated, do token exchange
            if (auth0Authenticated) {
                try {
                    const auth0Token = await getAccessTokenSilently();
                    const result = await exchangeToken(auth0Token);

                    if (result.requiresAccessRequest) {
                        // User has no tenant access - needs to request access
                        setRequiresAccessRequest(true);
                        setAccessRequestContext({
                            userId: result.userId!,
                            email: result.email!,
                            customerId: result.customerId ?? null,
                            temporaryToken: result.temporaryToken || auth0Token,
                        });
                    } else if (result.requiresTenantSelection && result.tenants) {
                        // Multi-tenant user - need tenant selection
                        setAvailableTenants(result.tenants);
                        setPendingTenantSelection(true);
                        setPendingAuth0Token(auth0Token);
                    } else if (result.accessToken) {
                        // Single tenant - authenticated
                        // Note: Refresh token is stored in httpOnly cookie by backend
                        setAccessToken(result.accessToken);
                        setUser(parseUserFromToken(result.accessToken));

                        // Store available tenants if provided
                        if (result.tenants) {
                            setAvailableTenants(result.tenants);
                        }
                    }
                } catch (e) {
                    setError(createAuthErrorInfo(e));
                }
            }

            setIsLoading(false);
        };

        initAuth();
    }, [
        auth0Loading,
        auth0Authenticated,
        getAccessTokenSilently,
        exchangeToken,
        parseUserFromToken,
        refreshAccessToken,
    ]);

    const value: AuthState = {
        user,
        availableTenants,
        isAuthenticated,
        isLoading: isLoading || auth0Loading,
        error,
        pendingTenantSelection,
        requiresAccessRequest,
        accessRequestContext,
        login,
        logout,
        selectTenant,
        switchTenant,
        refreshAccessToken,
        clearError,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error("useAuth must be used within an AuthProvider");
    }
    return context;
}
